import React from 'react';
import { renderToString } from 'react-dom/server';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Label,
  Input,
  FormGroup
} from 'reactstrap';
import LabelInput from '@src/components/LabelInput/LabelInput';
import EmailBody from './EmailBody/EmailBody';
import { required, multiEmail, faxNumber, maxLength } from '@src/validation';
import { Editor } from '@tinymce/tinymce-react';
import axiosInstance from '@src/axios-instance';
import CSS from './SendModal.module.scss';

const fieldValidator = {
  invoice_email: [required, multiEmail],
  email_cc: [multiEmail],
  email_bcc: [multiEmail],
  email_subject: [required],
  body: [],
  send_invoice: [],
  send_fax: [],
  fax_number: [faxNumber, value => maxLength(value, 255)]
};

const fieldFaxValidator = {
  invoice_email: [required, multiEmail],
  email_cc: [multiEmail],
  email_bcc: [multiEmail],
  email_subject: [required],
  body: [],
  send_invoice: [],
  send_fax: [],
  fax_number: [required, faxNumber, value => maxLength(value, 255)]
};

class MailingAddressModal extends React.Component {
  constructor(props) {
    super(props);
    const fields = {};

    Object.keys(fieldValidator).forEach(key => {
      fields[key] = {
        value:
          props.entity[key] !== undefined && props.entity[key] !== null
            ? props.entity[key]
            : '',
        visited: false,
        error: false
      };
    });

    fields.send_invoice.value =
      props.entity.qb_invoice_id === null || props.entity.status === 1 ? 0 : 1;

    fields.send_fax.value =
      props.entity.fax_number === null || props.entity.fax_number.length < 1
        ? 0
        : 1;

    if (
      props.entity.delivery_email !== null &&
      props.entity.delivery_email.length !== 0
    ) {
      fields.invoice_email.value = props.entity.delivery_email;
    }

    fields.body.value = renderToString(<EmailBody entity={props.entity} />);
    this.state = { ...fields };
  }

  setSubmitType = name => {
    this.setState({
      submitType: name
    });
  };

  updateForm = (event, name) => {
    const inputData = {
      value:
        event !== null && event.target !== undefined
          ? event.target.value
          : event,
      visited: true,
      error: false
    };

    const validator =
      this.state.send_fax.value === 0 ? fieldValidator : fieldFaxValidator;
    for (let i = 0; i < validator[name].length; i++) {
      const validationMsg = validator[name][i](inputData.value);
      if (validationMsg !== undefined) {
        inputData.error = validationMsg;
        break;
      }
    }

    this.setState({
      [name]: inputData
    });
  };

  submitHandler = e => {
    e.preventDefault();
    const formData = JSON.parse(JSON.stringify(this.state));
    const postData = {};
    let isValid = true;

    const validator =
      this.state.send_fax.value === 0 ? fieldValidator : fieldFaxValidator;

    Object.keys(validator).forEach(key => {
      postData[key] =
        formData[key].value.value !== undefined
          ? formData[key].value.value
          : formData[key].value;
      for (let i = 0; i < validator[key].length; i++) {
        const validationMsg = validator[key][i](formData[key].value);
        formData[key].visited = true;
        if (validationMsg !== undefined) {
          formData[key].error = validationMsg;
          isValid = false;
          break;
        } else {
          formData[key].error = false;
        }
      }
    });
    this.setState({ ...formData });

    if (isValid) {
      if (this.state.submitType === 'email') {
        this.props.loading(true);
        axiosInstance()
          .post(
            `/evidence-of-insurances/send/${this.props.entity.id}.json`,
            postData
          )
          .then(response => {
            this.props.getEntity();
            this.props.onSuccess('EOI email sent.');
            this.props.loading(false);
            this.props.toggle();
          })
          .catch(err => {
            alert(
              'Email authentication failed. Please contact your admin. Sender email and password information can be updated on Settings - Details view.'
            );
            this.props.loading(false);
          });
      } else if (this.state.submitType === 'schedule') {
        localStorage.setItem('schedulePost', JSON.stringify(postData));
        this.props.onScheduleClick();
      }
    }

    return false;
  };

  downloadPDF = (response, name) => {
    const byteCharacters = window.atob(response);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', name);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  downloadEmailAttachment = type => {
    this.props.loading(true);
    axiosInstance()
      .get(
        `/evidence-of-insurances/download-email-attachment/${this.props.entity.id}/${type}.json`
      )
      .then(response => {
        this.props.loading(false);
        this.downloadPDF(response.data.response, response.data.name);
      })
      .catch(err => {
        this.props.loading(false);
      });
  };

  downloadEOIsAttachment = entityId => {
    this.props.loading(true);
    axiosInstance()
      .get(`/attachments/${entityId}.json`)
      .then(response => {
        this.props.loading(false);
        this.downloadPDF(response.data.response, response.data.name);
      })
      .catch(err => {
        this.props.loading(false);
      });
  };

  render = () => {
    const attachments = [];
    const { entity } = this.props;
    let pdfName = '';
    if (entity.master_cert_only === 0) {
      const attachmentName =
        entity.unit !== null
          ? `${entity.unit.address_no}_${entity.unit.street}_${entity.unit.unit_no}.pdf`
          : `${entity.association_name}.pdf`;
      pdfName = attachmentName.replace(' ', '_');
      attachments.push(
        <span
          key={'eoi-pdf-attachment'}
          className={'pointer'}
          onClick={() => this.downloadEmailAttachment('eoi')}
        >
          {pdfName}
        </span>
      );
    }

    const borrowName =
      entity.owner !== null
        ? entity.owner
            .split(' ')
            .slice(-1)
            .pop()
        : '';
    if (entity.qb_invoice_id !== null && !entity.qb_invoice_removed) {
      if (entity.bill_to_type === 'escrow-company') {
        pdfName = `invoice_${entity.qb_invoice_number}_${entity.escrow_company.name}_${entity.association_name}_${borrowName}.pdf`
          .replace(/,/g, '')
          .replace(/ /g, '_');
      } else if (entity.bill_to_type === 'lender') {
        pdfName = `invoice_${entity.qb_invoice_number}_${entity.lender.name}_${entity.association_name}_${borrowName}.pdf`
          .replace(/,/g, '')
          .replace(/ /g, '_');
      } else {
        if (entity.unit.owner !== undefined) {
          pdfName = `invoice_${entity.qb_invoice_number}_${entity.unit.owner}_${entity.association_name}_${borrowName}.pdf`
            .replace(/,/g, '')
            .replace(/ /g, '_');
        } else {
          pdfName = `invoice_${entity.qb_invoice_number}_${entity.association_name}_${borrowName}.pdf`
            .replace(/,/g, '')
            .replace(/ /g, '_');
        }
      }
      if (attachments.length > 0) {
        attachments.push(', ');
      }

      attachments.push(
        <span
          key={'qb-invoice-attachment'}
          className={'pointer'}
          onClick={() => this.downloadEmailAttachment('qb-invoice')}
        >
          {pdfName}
        </span>
      );
    }

    if (entity.include_cover_letter === 1) {
      if (
        entity.association !== null &&
        entity.association.cover_letter !== null
      ) {
        pdfName = entity.association.cover_letter
          .replace(/^.*[/]/, '')
          .replace(' ', '_');

        if (attachments.length > 0) {
          attachments.push(', ');
        }
        attachments.push(
          <span
            key={'cover-letter-attachment'}
            className={'pointer'}
            onClick={() => this.downloadEmailAttachment('cover-letter')}
          >
            {pdfName}
          </span>
        );
      } else if (entity.tenant.cover_letter !== null) {
        pdfName = entity.tenant.cover_letter
          .replace(/^.*[/]/, '')
          .replace(' ', '_');
        if (attachments.length > 0) {
          attachments.push(', ');
        }
        attachments.push(
          <span
            key={'tenant-cover-letter'}
            className={'pointer'}
            onClick={() => this.downloadEmailAttachment('cover-letter')}
          >
            {pdfName}
          </span>
        );
      }
    }

    if (
      entity.include_sample_quote === 1 &&
      (entity.association.sample_quote !== null ||
        entity.association.sample_quote_region_id !== null)
    ) {
      pdfName =
        entity.association_name.replace(/^.*[/]/, '').replace(' ', '_') +
        '.pdf';
      if (attachments.length > 0) {
        attachments.push(', ');
      }
      attachments.push(
        <span
          key={'sample-quote-attachment'}
          className={'pointer'}
          onClick={() => this.downloadEmailAttachment('sample-quote')}
        >
          {pdfName}
        </span>
      );
    }

    if (entity.attachments.length > 0) {
      entity.attachments.map(item => {
        if (attachments.length > 0) {
          attachments.push(', ');
        }
        attachments.push(
          <span
            key={'sample-quote-attachment'}
            className={'pointer'}
            onClick={() => this.downloadEOIsAttachment(item.id)}
          >
            {item.file_name}
          </span>
        );
        return null;
      });
    }

    return (
      <Modal
        isOpen={this.props.isActive}
        toggle={this.props.toggle}
        className={this.props.className}
        size={'lg'}
      >
        <form
          ref={c => {
            this.form = c;
          }}
          onSubmit={this.submitHandler.bind(this)}
        >
          <ModalHeader toggle={this.props.toggle}>{'Email'}</ModalHeader>
          <div className={'position-relative'}>
            <ModalBody className={'p-4'}>
              <Row>
                <Col xs={12}>
                  <LabelInput
                    name={'Email Recipient (Separate emails with a comma)'}
                    type={'text'}
                    required={true}
                    {...this.state.invoice_email}
                    onChange={event => this.updateForm(event, 'invoice_email')}
                  />
                </Col>
                <Col xs={12}>
                  <LabelInput
                    name={'Cc'}
                    type={'text'}
                    {...this.state.email_cc}
                    onChange={event => this.updateForm(event, 'email_cc')}
                  />
                </Col>
                <Col xs={12}>
                  <LabelInput
                    name={'Bcc'}
                    type={'text'}
                    {...this.state.email_bcc}
                    onChange={event => this.updateForm(event, 'email_bcc')}
                  />
                </Col>
                <Col xs={12}>
                  <LabelInput
                    name={'Subject'}
                    type={'text'}
                    required={true}
                    {...this.state.email_subject}
                    onChange={event => this.updateForm(event, 'email_subject')}
                  />
                </Col>
                <Col xs={12} className={'pt-1'}>
                  <Editor
                    apiKey="4mz6kr57dqdqs584hnltw8r5555fnbt0m3p6ss5c9m7zubk1"
                    cloudChannel="dev"
                    initialValue={this.state.body.value}
                    init={{
                      height: 500
                    }}
                    onChange={event =>
                      this.updateForm(event.target.getContent(), 'body')
                    }
                  />
                </Col>
                <Col xs={12} className={'pt-2'}>
                  <span>
                    <strong>Attachment: </strong>
                    <span className={CSS.attachments}>{attachments}</span>
                  </span>
                </Col>
                <Col xs={12} className={'pt-2'}>
                  <FormGroup check className={'pb-1'}>
                    <Label check>
                      <Input
                        type="checkbox"
                        defaultChecked={this.state.send_invoice.value === 1}
                        disabled={
                          this.props.entity.qb_invoice_id === null ||
                          this.props.entity.qb_invoice_removed
                        }
                        onChange={event =>
                          this.updateForm(
                            event.target.checked ? 1 : 0,
                            'send_invoice'
                          )
                        }
                      />{' '}
                      Email invoice from QuickBooks Online at the same time.
                    </Label>
                  </FormGroup>
                </Col>

                <Col xs={12} className={'pt-2'}>
                  <FormGroup check className={'pb-1'}>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={this.state.send_fax.value === 1}
                        onChange={event =>
                          this.updateForm(
                            event.target.checked ? 1 : 0,
                            'send_fax'
                          )
                        }
                      />{' '}
                      Fax Evidence of Insurance document at the same time.
                    </Label>
                  </FormGroup>
                  {this.state.send_fax.value === 1 && (
                    <div className={'pl-4'}>
                      <LabelInput
                        name={'Fax (Separate fax numbers with a comma)'}
                        type={'text'}
                        {...this.state.fax_number}
                        onChange={event => this.updateForm(event, 'fax_number')}
                      />
                    </div>
                  )}
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter className={'py-4 justify-content-end'}>
              <Button
                color="primary"
                className={'btn px-3'}
                type={'submit'}
                onClick={() => this.setSubmitType('schedule')}
              >
                Schedule For Later
              </Button>
              <Button
                color="primary"
                className={'btn px-3'}
                type={'submit'}
                onClick={() => this.setSubmitType('email')}
              >
                Email Now
              </Button>
            </ModalFooter>
          </div>
        </form>
      </Modal>
    );
  };
}
export default MailingAddressModal;
